import { useInput } from 'react-admin';
import {
  Stack, Box, Typography, TextField, IconButton, Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { tokens } from '../theme.js';

const DEFAULT_VALUE = {
  eyebrow: '',
  rawTitle: '',
  offices: [],
  hoursTitle: '',
  hoursLines: [],
};

/**
 * Editor for /contact page right-rail copy stored at
 * SiteSettings.contactInfo.
 *
 * Shape:
 *   {
 *     eyebrow, rawTitle,
 *     offices: [{ title, subtitle, lines: [string, ...] }, ...],
 *     hoursTitle,
 *     hoursLines: [string, ...],
 *   }
 *
 * Each office's `lines` and the `hoursLines` array become a single block
 * of text on the page, with each entry on its own line.
 */
export function ContactInfoEditor({ source = 'contactInfo' }) {
  const { field } = useInput({ source });
  const value = field.value && typeof field.value === 'object' ? field.value : DEFAULT_VALUE;
  const offices = Array.isArray(value.offices) ? value.offices : [];
  const hoursLines = Array.isArray(value.hoursLines) ? value.hoursLines : [];

  const patch = (p) => field.onChange({ ...value, ...p });
  const setOffices = (next) => patch({ offices: next });
  const setHoursLines = (next) => patch({ hoursLines: next });

  // Offices use a textarea for `lines` (one line per row) — easier than
  // a nested editor and matches how the data renders.
  const linesToText = (arr) => (Array.isArray(arr) ? arr.join('\n') : '');
  const textToLines = (s) => String(s ?? '').split('\n');

  const setOffice = (i, p) => setOffices(offices.map((o, idx) => (idx === i ? { ...o, ...p } : o)));
  const addOffice = () => setOffices([...offices, { title: '', subtitle: '', lines: [] }]);
  const removeOffice = (i) => setOffices(offices.filter((_, idx) => idx !== i));
  const moveOffice = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= offices.length) return;
    const next = offices.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setOffices(next);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="caption" sx={{ color: tokens.ink3, fontSize: 12, lineHeight: 1.6 }}>
        Right-hand "Visit us" rail on the /contact page. Each office shows a title, subtitle and a block of address / phone / email lines.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          size="small"
          label="Section eyebrow"
          value={value.eyebrow || ''}
          onChange={(e) => patch({ eyebrow: e.target.value })}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label="Section title"
          value={value.rawTitle || ''}
          onChange={(e) => patch({ rawTitle: e.target.value })}
          helperText='Italic words wrapped in <em>…</em>'
          sx={{ flex: 1 }}
        />
      </Stack>

      {/* ----- Offices ----- */}
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink, mb: 0.5 }}>
          Office cards
        </Typography>

        <Stack spacing={1}>
          {offices.map((o, i) => (
            <Box
              key={i}
              sx={{
                p: 1.5,
                border: `1px solid ${tokens.line}`,
                borderRadius: 1,
                background: tokens.paper,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <TextField
                  size="small"
                  label="Title"
                  value={o.title || ''}
                  onChange={(e) => setOffice(i, { title: e.target.value })}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Subtitle"
                  value={o.subtitle || ''}
                  onChange={(e) => setOffice(i, { subtitle: e.target.value })}
                  helperText='E.g. "Play Group to Class X"'
                  sx={{ flex: 1 }}
                />
                <Stack direction="row" spacing={0.5}>
                  <IconButton size="small" onClick={() => moveOffice(i, -1)} disabled={i === 0}>
                    <ArrowUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => moveOffice(i, 1)} disabled={i === offices.length - 1}>
                    <ArrowDownIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => removeOffice(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              <TextField
                size="small"
                label="Lines (one per row — address, phone, email…)"
                value={linesToText(o.lines)}
                onChange={(e) => setOffice(i, { lines: textToLines(e.target.value) })}
                multiline
                minRows={3}
                fullWidth
              />
            </Box>
          ))}
        </Stack>

        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addOffice} sx={{ mt: 1.5 }}>
          Add office
        </Button>
      </Box>

      {/* ----- Hours ----- */}
      <Box sx={{ pt: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink, mb: 0.5 }}>
          Hours card
        </Typography>
        <Typography variant="caption" sx={{ color: tokens.ink3, fontSize: 12, lineHeight: 1.6, display: 'block', mb: 1.5 }}>
          The dark card at the bottom of the rail. Use <code>&lt;em&gt;…&lt;/em&gt;</code> inside a line to italicise.
        </Typography>

        <TextField
          size="small"
          label="Title"
          value={value.hoursTitle || ''}
          onChange={(e) => patch({ hoursTitle: e.target.value })}
          fullWidth
          sx={{ mb: 1.5 }}
        />

        <TextField
          size="small"
          label="Lines (one per row — empty rows allowed for spacing)"
          value={linesToText(hoursLines)}
          onChange={(e) => setHoursLines(textToLines(e.target.value))}
          multiline
          minRows={4}
          fullWidth
        />
      </Box>
    </Stack>
  );
}
